import type { Liturgy, Oraisons, AnimateurText, Catalog, CalendarItem, SongAiResult, Plan, PlanItem, StoredMass } from "../types";

async function jsonOrThrow<T>(r: Response): Promise<T> {
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((data as any)?.error || `Erreur ${r.status}`);
  return data as T;
}

/** Thrown on a 409 — someone else saved since we loaded. Carries the current value. */
export class ConflictError extends Error {
  current: any;
  constructor(current: any) {
    super("conflit");
    this.name = "ConflictError";
    this.current = current;
  }
}

export async function fetchLiturgy(date: string): Promise<Liturgy> {
  return jsonOrThrow<Liturgy>(await fetch(`/api/liturgy?date=${date}`));
}

export async function fetchOraisons(date: string): Promise<Oraisons> {
  // Never throws upward — orations are optional / best-effort.
  try {
    const r = await fetch(`/api/oraisons?date=${date}`);
    return (await r.json()) as Oraisons;
  } catch {
    return { source: "prionseneglise.fr", error: "indisponible" } as Oraisons;
  }
}

export async function generateAnimateur(
  liturgy: Liturgy,
  blocks?: string[]
): Promise<AnimateurText> {
  return jsonOrThrow<AnimateurText>(
    await fetch("/api/animateur", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ liturgy, blocks }),
    })
  );
}

export async function generateSongsAI(
  liturgy: Liturgy,
  repertoire: { title: string; moments: string[]; seasons: string[]; usedCount: number; category?: string }[],
  moments?: string[],
  carnetOnly?: boolean
): Promise<SongAiResult> {
  return jsonOrThrow<SongAiResult>(
    await fetch("/api/songs-ai", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ liturgy, repertoire, moments, carnetOnly }),
    })
  );
}

const normSong = (t: string) => (t || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").replace(/[^a-z0-9]+/g, " ").trim();

export async function fetchCatalog(): Promise<Catalog> {
  const base = await jsonOrThrow<Catalog>(await fetch("/data/songs.json"));
  // Merge the scraped parish repertoire (titles + partition/YouTube links). The
  // curated catalog wins on a title clash; new titles are appended.
  try {
    const extra = await fetch("/data/songs-mornantais.json").then((r) => (r.ok ? r.json() : null));
    if (extra?.songs?.length) {
      const seen = new Set(base.songs.map((s) => normSong(s.title)));
      for (const s of extra.songs) {
        const k = normSong(s.title);
        if (k && !seen.has(k)) { seen.add(k); base.songs.push(s); }
      }
    }
  } catch { /* repertoire is optional */ }
  return base;
}

// ---- Persistence (plan + drafts) ----
export async function fetchPlan(): Promise<Plan> {
  return jsonOrThrow<Plan>(await fetch("/api/plan", { cache: "no-store" }));
}

export async function savePlan(baseVersion: number, editor: string, sundays: PlanItem[]): Promise<Plan> {
  const r = await fetch("/api/plan", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ baseVersion, editor, sundays }),
  });
  if (r.status === 409) throw new ConflictError((await r.json()).current);
  return jsonOrThrow<Plan>(r);
}

export async function fetchMass(date: string): Promise<StoredMass | null> {
  const r = await fetch(`/api/mass?date=${date}`, { cache: "no-store" });
  if (r.status === 404) return null;
  return jsonOrThrow<StoredMass>(r);
}

export async function saveMass(date: string, baseVersion: number, editor: string, doc: unknown): Promise<StoredMass> {
  const r = await fetch(`/api/mass?date=${date}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ baseVersion, editor, doc }),
  });
  if (r.status === 409) throw new ConflictError((await r.json()).current);
  return jsonOrThrow<StoredMass>(r);
}

export async function deleteMass(date: string): Promise<void> {
  await fetch(`/api/mass?date=${date}`, { method: "DELETE" });
}

export async function fetchCalendar(from: string, count = 7): Promise<CalendarItem[]> {
  try {
    const data = await jsonOrThrow<{ items: CalendarItem[] }>(
      await fetch(`/api/calendar?from=${from}&count=${count}`)
    );
    return data.items || [];
  } catch {
    return [];
  }
}
